/**
 * Сборка упражнения.
 *
 * Алгоритм детерминирован: при одинаковом запросе и seed результат совпадает
 * посимвольно. Смесь материала задаётся снаружи (learning-engine), генератор
 * только честно её реализует и следит за инвариантами (ТЗ §16.2).
 */

import type { MaterialMix } from '@type2x/learning-engine';
import type { MasteryUnit } from '@type2x/shared-contracts';

import { createRng, type Rng } from './rng.js';
import {
  ITEM_ORIGINS,
  type CandidatePools,
  type Exercise,
  type ExerciseItem,
  type ExerciseRequest,
  type ItemOrigin,
} from './types.js';

/** Сколько элементов каждого происхождения нужно взять. */
export type OriginQuota = Readonly<Record<ItemOrigin, number>>;

/**
 * Распределяет itemCount по долям смеси.
 *
 * Остаток от округления отдаётся происхождениям с наибольшей дробной частью:
 * так сумма квот всегда точно равна itemCount, а не «примерно».
 */
export function allocateQuota(itemCount: number, mix: MaterialMix): OriginQuota {
  const exact = ITEM_ORIGINS.map((origin) => ({ origin, value: mix[origin] * itemCount }));
  const quota: Record<ItemOrigin, number> = {
    weak: 0,
    due: 0,
    fresh: 0,
    maintenance: 0,
    transfer: 0,
  };

  let assigned = 0;
  for (const { origin, value } of exact) {
    quota[origin] = Math.floor(value);
    assigned += quota[origin];
  }

  const remainders = exact
    .map(({ origin, value }) => ({ origin, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.origin.localeCompare(b.origin));

  let index = 0;
  while (assigned < itemCount && remainders.length > 0) {
    const entry = remainders[index % remainders.length]!;
    quota[entry.origin] += 1;
    assigned += 1;
    index += 1;
  }

  return quota;
}

function isAllowed(unit: MasteryUnit, allowed: ReadonlySet<string> | undefined): boolean {
  if (allowed === undefined) return true;
  return [...unit.content.toLowerCase()].every((character) => allowed.has(character));
}

/**
 * Берёт из пула нужное число единиц.
 *
 * Пул отсортирован по важности, поэтому сначала берётся голова списка, и
 * только при нехватке материала выбор дополняется случайными элементами:
 * иначе упражнение оказалось бы короче заказанного.
 */
function takeFromPool(
  pool: readonly MasteryUnit[],
  count: number,
  allowed: ReadonlySet<string> | undefined,
  rng: Rng,
): MasteryUnit[] {
  const usable = pool.filter((unit) => isAllowed(unit, allowed));
  if (usable.length === 0 || count <= 0) return [];

  const taken = usable.slice(0, count);
  while (taken.length < count) {
    taken.push(rng.pick(usable));
  }
  return taken;
}

/**
 * Перемешивает элементы так, чтобы один и тот же текст не повторялся подряд
 * больше разрешённого числа раз (ТЗ FR-EN-06).
 */
export function spaceOutRepeats(
  items: readonly ExerciseItem[],
  maxConsecutive: number,
  rng: Rng,
): ExerciseItem[] {
  const shuffled = rng.shuffle(items);
  const result: ExerciseItem[] = [];
  const pending = [...shuffled];

  while (pending.length > 0) {
    const tailRun = countTailRun(result);
    const index =
      tailRun >= maxConsecutive
        ? pending.findIndex((item) => item.text !== result[result.length - 1]?.text)
        : 0;

    // Если альтернативы нет, материала слишком мало — берём что есть.
    const chosen = index === -1 ? 0 : index;
    result.push(pending[chosen]!);
    pending.splice(chosen, 1);
  }

  return result;
}

function countTailRun(items: readonly ExerciseItem[]): number {
  if (items.length === 0) return 0;
  const last = items[items.length - 1]!.text;
  let run = 0;
  for (let index = items.length - 1; index >= 0 && items[index]!.text === last; index -= 1) {
    run += 1;
  }
  return run;
}

function collectItems(
  pools: CandidatePools,
  quota: OriginQuota,
  allowed: ReadonlySet<string> | undefined,
  rng: Rng,
): ExerciseItem[] {
  const items: ExerciseItem[] = [];

  for (const origin of ITEM_ORIGINS) {
    for (const unit of takeFromPool(pools[origin], quota[origin], allowed, rng)) {
      items.push({
        text: unit.content,
        origin,
        unitId: origin === 'transfer' ? null : unit.id,
      });
    }
  }

  return items;
}

/** Собирает упражнение по запросу. */
export function generateExercise(request: ExerciseRequest): Exercise {
  const rng = createRng(request.seed);
  const quota = allocateQuota(request.itemCount, request.mix);
  const collected = collectItems(request.pools, quota, request.allowedCharacters, rng);
  const items = spaceOutRepeats(collected, request.maxConsecutiveRepeats, rng);

  const total = items.length || 1;
  const actualMix: MaterialMix = {
    weak: items.filter((item) => item.origin === 'weak').length / total,
    due: items.filter((item) => item.origin === 'due').length / total,
    fresh: items.filter((item) => item.origin === 'fresh').length / total,
    maintenance: items.filter((item) => item.origin === 'maintenance').length / total,
    transfer: items.filter((item) => item.origin === 'transfer').length / total,
  };

  return {
    seed: request.seed,
    mode: request.mode,
    language: request.language,
    layoutId: request.layoutId,
    items,
    text: items.map((item) => item.text).join(' '),
    hintLevel: request.hintLevel,
    mix: actualMix,
  };
}
